import { AppServices } from '../../contexts/AppServicesContext';
import { createDomainContext, DomainContext } from '../changes/DomainContext';
import { Patient } from '../patient/patient';
import { completeHydration } from '../patient/hydration';
import { EngineAdapter } from '../adapters/EngineAdapter';
import {
    LoggingPersistenceService,
    PersistenceService,
} from '../adapters/LoggingPersistenceService';
import { ContourService } from '../services/structureSet/ContourService';
import { RoiService } from '../services/structureSet/RoiService';
import { StructureSetExportService } from '../services/structureSet/StructureSetExportService';
import { StructureSetService } from '../services/structureSet/StructureSetService';
import { DisplayPreferencesStore } from './DisplayPreferencesStore';
import { domainTrace } from '../diagnostics/domainTrace';
// RecoilBridge wired in later tasks

export interface PatientSession {
    readonly context: DomainContext;
    readonly patient: Patient;
    readonly structureSetService: StructureSetService;
    readonly structureSetExportService: StructureSetExportService;
    readonly roiService: RoiService;
    readonly contourService: ContourService;
    readonly displayPreferences: DisplayPreferencesStore;
    readonly engineAdapter: EngineAdapter;
    readonly persistenceService: PersistenceService;
    dispose(): void;
}

export function createPatientSession(
    appServices: AppServices,
    patient: Patient
): PatientSession {
    completeHydration(patient);
    const context = createDomainContext();
    patient.attachTracker(context.tracker);
    domainTrace.session('create', { patientId: patient.id });

    const structureSetService = new StructureSetService(context, patient);
    const structureSetExportService = new StructureSetExportService(patient);
    const roiService = new RoiService(context, patient);
    const contourService = new ContourService(context, patient);
    const displayPreferences = new DisplayPreferencesStore();
    const engineAdapter = new EngineAdapter(appServices.engine, context.store);
    const persistenceService = new LoggingPersistenceService(context.store);

    const disposers: Array<() => void> = [
        () => engineAdapter.dispose(),
        () => persistenceService.dispose(),
    ];
    let disposed = false;

    return {
        context,
        patient,
        structureSetService,
        structureSetExportService,
        roiService,
        contourService,
        displayPreferences,
        engineAdapter,
        persistenceService,
        // Idempotent: useUnloadPatient disposes synchronously, then
        // PatientSessionProvider's effect cleanup runs on the next
        // render and would otherwise dispose a second time.
        dispose() {
            if (disposed) {
                return;
            }
            disposed = true;
            domainTrace.session('dispose', { patientId: patient.id });
            for (let i = disposers.length - 1; i >= 0; i--) {
                disposers[i]();
            }
        },
    };
}
